const libFableServiceProviderBase = require('fable-serviceproviderbase');
const libWorkbookWriter = require('./Workbook-Writer.js');

const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'DashboardExporter',
};

/**
 * Exports the currently-rendered dashboard's bucket records as CSV or as a
 * mildly-styled Excel file.
 *
 * Reads from `pict.AppData.WalbecRecords` (parsed Datum) and the active
 * dashboard's manifest at
 * `pict.PictSectionRecordSet.manifestDefinitions[pict.AppData.WalbecActiveDashboard]`.
 *
 * Column resolution: the dashboard manifest's `ReferenceManifests.<X>.Descriptors`
 * describes the detail-table columns. Each descriptor has `Hash`, `Name`,
 * `DataAddress` (path into a bucket record). Excellent already for export —
 * we use it to drive the column list, header labels, and per-row value lookup.
 *
 * The Excel output is HTML-as-Excel — a styled `<table>` saved with `.xls`
 * extension and `application/vnd.ms-excel` MIME. Excel respects inline CSS
 * for bold, color, borders, alignment and treats it as a one-sheet workbook.
 * Avoids bundling exceljs (~950KB) for "mildly styled" requirements.
 */
class DashboardExporter extends libFableServiceProviderBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _DEFAULT_CONFIGURATION, pOptions);
		super(pFable, tmpOptions, pServiceHash);
		/** @type {any} */
		this.pict = this.fable;
		/** @type {any} */
		this.log;
	}

	/**
	 * Map a ReferenceManifest's descriptor set to export columns. Skips the
	 * synthetic drill-down link cell (a URL, not data); Templated cells export
	 * their underlying DataAddress value.
	 *
	 * @param {Record<string, any>} pDescriptors
	 * @return {Array<{ Hash:string, Label:string, Path:string, DataType:string, DecimalPrecision:number|null }>}
	 */
	_columnsFromDescriptors(pDescriptors)
	{
		const tmpColumns = [];
		for (const tmpAddress of Object.keys(pDescriptors || {}))
		{
			const tmpDesc = pDescriptors[tmpAddress];
			if (tmpDesc && tmpDesc.PictForm && tmpDesc.PictForm.InputType === 'Link') continue;
			const tmpPictForm = tmpDesc.PictForm || {};
			tmpColumns.push(
				{
					Hash: tmpDesc.Hash,
					Label: tmpDesc.Name || tmpDesc.Hash || tmpAddress,
					Path: tmpDesc.DataAddress || tmpAddress,
					DataType: tmpDesc.DataType || 'String',
					// Carried for the Excel export: measurements are stored as STRINGS
					// throughout this system, deliberately, so arbitrary precision never
					// meets JavaScript's float mantissa. The workbook is the one place a
					// real number is required, and this is the precision to round to on
					// the way out. CSV keeps the untouched string.
					DecimalPrecision: (typeof tmpPictForm.DecimalPrecision === 'number') ? tmpPictForm.DecimalPrecision : null,
				});
		}
		return tmpColumns;
	}

	/**
	 * Resolve the export units for the active route — one unit per on-screen
	 * detail table, each pairing a column list with the records it renders:
	 *
	 *  1. **Form-mode dashboard** (`/SpecificDashboard/...`). The manifest
	 *     already declares every detail table: each `Sections[].Groups[]`
	 *     entry with `Layout: 'Tabular'` names its row template
	 *     (`RecordManifest` → `ReferenceManifests[...].Descriptors` = the
	 *     columns) and its data address (`RecordSetAddress` → the AppData
	 *     array the aggregation solvers populate = the records). One unit per
	 *     tabular group, so multi-table dashboards (e.g. Stockpile
	 *     Verification's stats + samples) export each table. Older manifests
	 *     with no tabular groups fall back to the legacy pairing (first
	 *     ReferenceManifest + AppData.WalbecDailyRecords) with a warning.
	 *
	 *  2. **List-mode dashboard / drilled document list** (`/List/...`).
	 *     One unit: columns from the recordset configuration's
	 *     `RecordSetListColumns`, records from
	 *     `pict.Bundle._ActiveFilterState[recordSet].Records`.
	 *
	 * Returns an empty array if no source resolves (e.g. on the chooser).
	 *
	 * @return {Array<{ title: string, groupName: string|null, columns: Array<{ Hash:string, Label:string, Path:string, DataType:string }>, records: Array<Record<string, any>>, recordsAddress: string|null }>}
	 */
	_resolveDashboardExportUnits()
	{
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
		const tmpDashMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\/SpecificDashboard\/([^\/]+)/);
		const tmpListMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\/List/);

		// Form-mode dashboard — one unit per tabular detail group in the manifest.
		if (tmpDashMatch)
		{
			const tmpScope = tmpDashMatch[2];
			const tmpManifest = this.pict.PictSectionRecordSet &&
				this.pict.PictSectionRecordSet.manifestDefinitions &&
				this.pict.PictSectionRecordSet.manifestDefinitions[tmpScope];
			if (!tmpManifest)
			{
				this.log.warn(`DashboardExporter: no manifest registered for dashboard [${tmpScope}].`);
				return [];
			}
			const tmpTitle = tmpManifest.Title || tmpScope;
			const tmpRefManifests = tmpManifest.ReferenceManifests || {};
			const tmpUnits = [];
			const tmpSections = Array.isArray(tmpManifest.Sections) ? tmpManifest.Sections : [];
			for (const tmpSection of tmpSections)
			{
				const tmpGroups = Array.isArray(tmpSection && tmpSection.Groups) ? tmpSection.Groups : [];
				for (const tmpGroup of tmpGroups)
				{
					if (!tmpGroup || tmpGroup.Layout !== 'Tabular' || !tmpGroup.RecordSetAddress || !tmpGroup.RecordManifest) continue;
					const tmpRefManifest = tmpRefManifests[tmpGroup.RecordManifest];
					if (!tmpRefManifest || !tmpRefManifest.Descriptors)
					{
						this.log.warn(`DashboardExporter: tabular group [${tmpGroup.Hash || tmpGroup.Name}] names RecordManifest [${tmpGroup.RecordManifest}] but the manifest has no such ReferenceManifest; skipping.`);
						continue;
					}
					const tmpRecords = this._readField(this.pict.AppData, tmpGroup.RecordSetAddress);
					if (!Array.isArray(tmpRecords) || tmpRecords.length === 0)
					{
						this.log.warn(`DashboardExporter: no records at AppData.${tmpGroup.RecordSetAddress} for tabular group [${tmpGroup.Hash || tmpGroup.Name}] — exporting headers only.`);
					}
					tmpUnits.push(
						{
							title: tmpTitle,
							groupName: tmpGroup.Name || tmpGroup.Hash || tmpGroup.RecordManifest,
							columns: this._columnsFromDescriptors(tmpRefManifest.Descriptors),
							records: Array.isArray(tmpRecords) ? tmpRecords : [],
							recordsAddress: tmpGroup.RecordSetAddress,
						});
				}
			}
			if (tmpUnits.length > 0)
			{
				return tmpUnits;
			}
			// Legacy fallback: a manifest with no declared tabular groups (pre-RecordSetAddress
			// shape). Pair the first ReferenceManifest with the historical roll-up address.
			const tmpKey = Object.keys(tmpRefManifests)[0];
			if (!tmpKey)
			{
				this.log.warn(`DashboardExporter: manifest [${tmpScope}] declares no tabular groups and no ReferenceManifests; nothing to export.`);
				return [];
			}
			this.log.warn(`DashboardExporter: manifest [${tmpScope}] declares no tabular groups; falling back to first ReferenceManifest [${tmpKey}] + AppData.WalbecDailyRecords.`);
			return [
				{
					title: tmpTitle,
					groupName: null,
					columns: this._columnsFromDescriptors((tmpRefManifests[tmpKey] && tmpRefManifests[tmpKey].Descriptors) || {}),
					records: Array.isArray(this.pict.AppData.WalbecDailyRecords) ? this.pict.AppData.WalbecDailyRecords : [],
					recordsAddress: 'WalbecDailyRecords',
				} ];
		}

		// List mode — read columns from the recordset config and records from
		// the active filter state.
		if (tmpListMatch)
		{
			const tmpRecordSet = tmpListMatch[1];
			const tmpConfig = this.pict.PictSectionRecordSet &&
				this.pict.PictSectionRecordSet.recordSetProviderConfigurations &&
				this.pict.PictSectionRecordSet.recordSetProviderConfigurations[tmpRecordSet];
			if (!tmpConfig) return [];
			const tmpListColumns = Array.isArray(tmpConfig.RecordSetListColumns) ? tmpConfig.RecordSetListColumns : [];
			const tmpColumns = tmpListColumns.map((pCol) =>
			{
				if (typeof pCol === 'string') return { Hash: pCol, Label: pCol, Path: pCol, DataType: 'String' };
				return {
					Hash: pCol.Key || pCol.Hash || pCol.DisplayName,
					Label: pCol.DisplayName || pCol.Key || pCol.Hash || '',
					Path: pCol.Key || pCol.Hash,
					DataType: pCol.DataType || 'String',
				};
			});
			const tmpRecords = (this.pict.Bundle && this.pict.Bundle._ActiveFilterState &&
				this.pict.Bundle._ActiveFilterState[tmpRecordSet] &&
				this.pict.Bundle._ActiveFilterState[tmpRecordSet].Records) || [];
			return [ { title: tmpConfig.Title || tmpRecordSet, groupName: null, columns: tmpColumns, records: Array.isArray(tmpRecords) ? tmpRecords : [], recordsAddress: null } ];
		}

		return [];
	}

	/**
	 * Back-compat single-table resolution (the Excel export renders one
	 * sheet): the first export unit, or null when nothing resolves.
	 *
	 * @return {{ title: string, columns: Array<{ Hash:string, Label:string, Path:string, DataType:string }>, records: Array<Record<string, any>> } | null}
	 */
	_resolveActiveDashboardColumns()
	{
		const tmpUnits = this._resolveDashboardExportUnits();
		return (tmpUnits.length > 0) ? tmpUnits[0] : null;
	}

	/**
	 * Read a value from a record using the manyfest hash resolver — supports
	 * dotted paths into Datum, arrays via numeric index, and `.length`.
	 *
	 * @param {any} pRecord
	 * @param {string} pPath
	 */
	_readField(pRecord, pPath)
	{
		if (!pPath) return '';
		try
		{
			const tmpUtility = this.pict && this.pict.fable && this.pict.fable.Utility;
			if (tmpUtility && typeof tmpUtility.getValueByHash === 'function')
			{
				const tmpValue = tmpUtility.getValueByHash(pRecord, pPath);
				return tmpValue == null ? '' : tmpValue;
			}
		}
		catch (pError)
		{/* fall through to manual */}
		let tmpRef = pRecord;
		for (const tmpKey of String(pPath).split('.'))
		{
			if (tmpRef == null) return '';
			tmpRef = tmpRef[tmpKey];
		}
		return tmpRef == null ? '' : tmpRef;
	}

	/**
	 * @param {string} pValue
	 */
	_csvEscape(pValue)
	{
		const tmpStr = String(pValue == null ? '' : pValue);
		if (/[",\n\r]/.test(tmpStr))
		{
			return `"${tmpStr.replace(/"/g, '""')}"`;
		}
		return tmpStr;
	}

	/**
	 * Trigger a browser download of `pContent` as `pFileName` with `pMime`.
	 *
	 * @param {string} pContent
	 * @param {string} pFileName
	 * @param {string} pMime
	 */
	_downloadBlob(pContent, pFileName, pMime)
	{
		const tmpBlob = new Blob([ pContent ], { type: pMime });
		const tmpURL = URL.createObjectURL(tmpBlob);
		const tmpAnchor = document.createElement('a');
		tmpAnchor.href = tmpURL;
		tmpAnchor.download = pFileName;
		document.body.appendChild(tmpAnchor);
		tmpAnchor.click();
		document.body.removeChild(tmpAnchor);
		setTimeout(() => URL.revokeObjectURL(tmpURL), 1000);
	}

	/**
	 * Filename-safe version of the dashboard title plus today's date.
	 *
	 * @param {string} pBase
	 * @param {string} pExtension
	 */
	_buildFileName(pBase, pExtension)
	{
		const tmpSlug = String(pBase || 'dashboard')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '');
		const tmpStamp = new Date().toISOString().slice(0, 10);
		return `${tmpSlug}-${tmpStamp}.${pExtension}`;
	}

	/**
	 * Public entry point — export the active dashboard's on-screen detail
	 * records as CSV, ONE FILE PER DETAIL TABLE. Single-table dashboards
	 * download `<title>-<date>.csv`; multi-table dashboards (e.g. Stockpile
	 * Verification's stats + samples) download `<title>-<group>-<date>.csv`
	 * per table, staggered so the browser doesn't throttle the burst.
	 */
	exportCSV()
	{
		const tmpUnits = this._resolveDashboardExportUnits();
		if (tmpUnits.length === 0)
		{
			this.log.warn('DashboardExporter.exportCSV: no exportable dashboard tables resolved for this route.');
			return;
		}
		tmpUnits.forEach((pUnit, pIndex) =>
		{
			const tmpHeader = pUnit.columns.map((pC) => this._csvEscape(pC.Label)).join(',');
			const tmpLines = pUnit.records.map((pRec) =>
				pUnit.columns.map((pCol) => this._csvEscape(this._readField(pRec, pCol.Path))).join(','),
			);
			const tmpCSV = [ tmpHeader ].concat(tmpLines).join('\r\n');
			const tmpFileBase = (tmpUnits.length > 1 && pUnit.groupName) ? `${pUnit.title} - ${pUnit.groupName}` : pUnit.title;
			const tmpFileName = this._buildFileName(tmpFileBase, 'csv');
			// Stagger the downloads — several synchronous anchor clicks in one tick
			// get coalesced/blocked by browser multi-download throttling.
			setTimeout(() =>
			{
				// Prepend a UTF-8 BOM so Excel's auto-import detects encoding correctly.
				this._downloadBlob('﻿' + tmpCSV, tmpFileName, 'text/csv;charset=utf-8');
				this.log.info(`DashboardExporter.exportCSV: ${pUnit.records.length} record(s) → ${tmpFileName}`);
			}, pIndex * 300);
		});
	}

	/**
	 * Round a DECIMAL STRING to a given number of places without ever letting the
	 * value become a float first.
	 *
	 * This matters more here than it looks. Measurements in this system are stored
	 * as STRINGS on purpose -- the lake columns are Text, the solvers pass them
	 * through as text -- precisely so arbitrary precision never meets JavaScript's
	 * float mantissa. Gmm arrives as values like "2.47770392749244712991", twenty
	 * significant digits, well past what a double holds. Calling Number() on that
	 * and then rounding would reintroduce exactly the drift the string storage
	 * exists to prevent. So the rounding is done on the digits, half-up, and only
	 * the already-short result is handed to Number().
	 *
	 * @param {string} pValue - a numeric string.
	 * @param {number} pPlaces - decimal places to keep.
	 * @return {string} The rounded decimal string.
	 */
	_roundDecimalString(pValue, pPlaces)
	{
		let tmpText = String(pValue).trim();
		let tmpSign = '';
		if ((tmpText.charAt(0) === '-') || (tmpText.charAt(0) === '+'))
		{
			tmpSign = (tmpText.charAt(0) === '-') ? '-' : '';
			tmpText = tmpText.slice(1);
		}
		const tmpPoint = tmpText.indexOf('.');
		if (tmpPoint < 0) { return tmpSign + tmpText; }
		let tmpWhole = tmpText.slice(0, tmpPoint);
		let tmpFraction = tmpText.slice(tmpPoint + 1);
		if (tmpFraction.length <= pPlaces) { return tmpSign + tmpText; }

		const tmpKept = tmpFraction.slice(0, pPlaces);
		const tmpRoundUp = (tmpFraction.charAt(pPlaces) >= '5');
		if (!tmpRoundUp) { return tmpSign + (pPlaces > 0 ? `${tmpWhole}.${tmpKept}` : tmpWhole); }

		// Carry through the kept digits, then into the whole part if it overflows.
		const tmpDigits = (tmpWhole + tmpKept).split('');
		let tmpIndex = tmpDigits.length - 1;
		while (tmpIndex >= 0)
		{
			if (tmpDigits[tmpIndex] === '9') { tmpDigits[tmpIndex] = '0'; tmpIndex--; }
			else { tmpDigits[tmpIndex] = String(Number(tmpDigits[tmpIndex]) + 1); break; }
		}
		if (tmpIndex < 0) { tmpDigits.unshift('1'); }
		const tmpCarried = tmpDigits.join('');
		const tmpSplit = tmpCarried.length - pPlaces;
		const tmpNewWhole = tmpCarried.slice(0, tmpSplit) || '0';
		const tmpNewFraction = tmpCarried.slice(tmpSplit);
		return tmpSign + (pPlaces > 0 ? `${tmpNewWhole}.${tmpNewFraction}` : tmpNewWhole);
	}

	/**
	 * The value to write into a workbook cell.
	 *
	 * A cell becomes a REAL NUMBER only when the column declares a numeric type and
	 * the value actually parses as one -- otherwise it stays text. That is the whole
	 * fidelity win over the old HTML-as-Excel output, which handed Excel every value
	 * as a string so nothing could be summed or pivoted.
	 *
	 * Anything that is not numeric, or a numeric column holding something odd, is
	 * passed through untouched rather than coerced: a blank must not become 0, and a
	 * stray '-' must not become NaN.
	 *
	 * @param {*} pValue @param {{DataType:string, DecimalPrecision:number|null}} pColumn
	 * @return {string|number}
	 */
	_toWorkbookValue(pValue, pColumn)
	{
		const tmpRaw = (pValue === null || typeof pValue === 'undefined') ? '' : String(pValue).trim();
		if (tmpRaw === '') { return ''; }
		const tmpType = String((pColumn && pColumn.DataType) || '').toLowerCase();
		const tmpNumeric = (tmpType === 'integer') || (tmpType === 'precisenumber') ||
			(tmpType === 'number') || (tmpType === 'float') || (tmpType === 'decimal');
		if (!tmpNumeric) { return tmpRaw; }
		// Plain decimal only. Exponent notation, currency marks and thousands
		// separators all stay as text rather than being silently reinterpreted.
		if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(tmpRaw)) { return tmpRaw; }
		const tmpPlaces = (pColumn && (typeof pColumn.DecimalPrecision === 'number')) ? pColumn.DecimalPrecision : null;
		const tmpRounded = (tmpPlaces === null) ? tmpRaw : this._roundDecimalString(tmpRaw, tmpPlaces);
		const tmpNumber = Number(tmpRounded);
		return isFinite(tmpNumber) ? tmpNumber : tmpRaw;
	}

	/**
	 * The provenance sheet: what this workbook covers.
	 *
	 * A workbook emailed to a DOT reviewer is as much a submittal as the printed
	 * page, and is just as meaningless without stating which mix design, which site
	 * and which dates produced it. Same provenance the print header carries, read
	 * from the live clause state rather than the URL so it reflects what actually
	 * generated the numbers.
	 *
	 * @param {Array<object>} pUnits
	 * @return {{name:string, columns:Array<object>, rows:Array<Array<*>>}}
	 */
	_buildReportInfoSheet(pUnits)
	{
		const tmpRows = [];
		const tmpTitle = (pUnits[0] && pUnits[0].title) || 'Dashboard';
		tmpRows.push([ 'Dashboard', tmpTitle ]);

		const tmpLoader = this.pict.providers.DynamicDashboardLoader;
		const tmpContext = (tmpLoader && typeof tmpLoader._resolveDashboardRouteContext === 'function')
			? tmpLoader._resolveDashboardRouteContext() : null;
		if (tmpContext && tmpContext.RecordSet && tmpLoader && (typeof tmpLoader._resolvePrintFilterSummary === 'function'))
		{
			const tmpSummary = tmpLoader._resolvePrintFilterSummary(tmpContext.RecordSet);
			// Said out loud either way: an unfiltered export is a different document
			// from a filtered one and must not look identical to one.
			tmpRows.push([ 'Filters', tmpSummary || 'None applied - covers ALL records' ]);
		}

		for (const tmpUnit of pUnits)
		{
			tmpRows.push([ tmpUnit.groupName ? `Rows (${tmpUnit.groupName})` : 'Rows', tmpUnit.records.length ]);
		}

		const tmpNow = new Date();
		const tmpStamp = `${tmpNow.getFullYear()}-${String(tmpNow.getMonth() + 1).padStart(2, '0')}-${String(tmpNow.getDate()).padStart(2, '0')}`;
		tmpRows.push([ 'Generated', tmpStamp ]);

		return { name: 'Report Info', columns: [ { Label: 'Field' }, { Label: 'Value' } ], rows: tmpRows };
	}

	/**
	 * Public entry point — export the active dashboard as a real .xlsx workbook.
	 *
	 * One workbook, one worksheet per on-screen detail table, plus a Report Info
	 * sheet. This replaces an HTML-as-Excel output (a styled <table> saved with an
	 * .xls extension) that modern Excel warns about on open and that handed every
	 * value over as text.
	 *
	 * @return {boolean} False when there is nothing on this route to export.
	 */
	exportExcel()
	{
		const tmpUnits = this._resolveDashboardExportUnits();
		if (!tmpUnits || (tmpUnits.length < 1))
		{
			this.log.warn('DashboardExporter.exportExcel: no exportable dashboard tables resolved for this route.');
			return false;
		}

		const tmpSheets = [ this._buildReportInfoSheet(tmpUnits) ];
		for (const tmpUnit of tmpUnits)
		{
			tmpSheets.push(
				{
					name: tmpUnit.groupName || 'Data',
					columns: tmpUnit.columns.map((pColumn) => ({ Label: pColumn.Label })),
					rows: tmpUnit.records.map((pRecord) =>
						tmpUnit.columns.map((pColumn) => this._toWorkbookValue(this._readField(pRecord, pColumn.Path), pColumn))),
				});
		}

		const tmpBytes = libWorkbookWriter.buildXLSX(tmpSheets);
		const tmpFileName = this._buildFileName(tmpUnits[0].title, 'xlsx');
		this._downloadBlob(tmpBytes, tmpFileName,
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
		const tmpTotal = tmpUnits.reduce((pSum, pUnit) => pSum + pUnit.records.length, 0);
		this.log.info(`DashboardExporter.exportExcel: ${tmpSheets.length} sheet(s), ${tmpTotal} record(s) -> ${tmpFileName}`);
		return true;
	}

	/**
	 * Public entry point — export the RAW, unaggregated, unfiltered backing rows
	 * (the lake table behind the current dashboard) as CSV. Where exportCSV() exports
	 * the on-screen per-day roll-up, this fetches the source rows straight from the
	 * lake Entity (scope = Deleted=0 only, no user filter clauses) and dumps every
	 * column — for offline pivoting in Excel. Cheap: one filtered fetch, all columns
	 * derived from the returned rows.
	 *
	 * @return {boolean} - False if not on a dashboard route or the recordset config is missing.
	 */
	exportRawCSV()
	{
		const tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) || '';
		const tmpMatch = tmpHash.match(/^#\/PSRS\/([^\/]+)\/SpecificDashboard\/([^\/]+)/);
		if (!tmpMatch)
		{
			this.log.warn('DashboardExporter.exportRawCSV: not on a SpecificDashboard route.');
			return false;
		}
		const tmpRecordSet = tmpMatch[1];
		const tmpScope = tmpMatch[2];
		const tmpConfig = this.pict.PictSectionRecordSet &&
			this.pict.PictSectionRecordSet.recordSetProviderConfigurations &&
			this.pict.PictSectionRecordSet.recordSetProviderConfigurations[tmpRecordSet];
		if (!tmpConfig || !tmpConfig.RecordSetMeadowEntity)
		{
			this.log.warn(`DashboardExporter.exportRawCSV: no recordset config / entity for ${tmpRecordSet}.`);
			return false;
		}
		const tmpEntity = tmpConfig.RecordSetMeadowEntity;
		const tmpURLPrefix = tmpConfig.RecordSetURLPrefix || '/1.0/';
		// Unfiltered/unaggregated: the recordset's default filter is scope + sort
		// (FBV~Deleted~EQ~0~FSF~BucketDate~ASC~0) with no user clauses — exactly the raw set.
		const tmpScopeFilter = tmpConfig.RecordSetDefaultFilter || 'FBV~Deleted~EQ~0';
		const tmpManifest = this.pict.PictSectionRecordSet.manifestDefinitions &&
			this.pict.PictSectionRecordSet.manifestDefinitions[tmpScope];
		const tmpTitle = (tmpManifest && tmpManifest.Title) || tmpConfig.Title || tmpRecordSet;
		const tmpCap = 25000;
		this.pict.EntityProvider.getEntitySetPage(
			tmpEntity,
			tmpScopeFilter,
			0,
			tmpCap,
			(pError, pRecords) =>
			{
				const tmpRecords = Array.isArray(pRecords) ? pRecords : [];
				if (pError)
				{
					this.log.warn(`DashboardExporter.exportRawCSV: fetch failed for ${tmpEntity}: ${pError.message || pError}`);
					return;
				}
				if (tmpRecords.length === 0)
				{
					this.log.warn('DashboardExporter.exportRawCSV: no backing rows returned.');
					return;
				}
				// Columns = union of keys across the returned rows (lake rows share a schema,
				// but union is safe). Object/array cells are JSON-stringified.
				const tmpColumns = [];
				const tmpSeen = {};
				for (const tmpRec of tmpRecords)
				{
					if (!tmpRec || typeof tmpRec !== 'object') continue;
					for (const tmpKey of Object.keys(tmpRec))
					{
						if (!tmpSeen[tmpKey])
						{
							tmpSeen[tmpKey] = true; tmpColumns.push(tmpKey);
						}
					}
				}
				const tmpHeader = tmpColumns.map((pK) => this._csvEscape(pK)).join(',');
				const tmpLines = tmpRecords.map((pRec) =>
					tmpColumns.map((pK) =>
					{
						let tmpValue = pRec ? pRec[pK] : '';
						if (tmpValue && typeof tmpValue === 'object')
						{
							try
							{
								tmpValue = JSON.stringify(tmpValue);
							}
							catch (pErr)
							{
								tmpValue = '';
							}
						}
						return this._csvEscape(tmpValue == null ? '' : tmpValue);
					}).join(','));
				const tmpCSV = [ tmpHeader ].concat(tmpLines).join('\r\n');
				const tmpFileName = this._buildFileName(`${tmpTitle} - raw backing data`, 'csv');
				// UTF-8 BOM so Excel auto-detects encoding.
				this._downloadBlob('﻿' + tmpCSV, tmpFileName, 'text/csv;charset=utf-8');
				const tmpCapNote = tmpRecords.length >= tmpCap ? ` (capped at ${tmpCap} rows)` : '';
				this.log.info(`DashboardExporter.exportRawCSV: ${tmpRecords.length} raw row(s)${tmpCapNote} → ${tmpFileName}`);
			},
			'',
			tmpURLPrefix,
		);
		return true;
	}
}

module.exports = DashboardExporter;
module.exports.default_configuration = _DEFAULT_CONFIGURATION;
